"use client";

import { graphql, readFragment } from "@opensea/graphql";
import { EmptyState, PausedHoverable, ScrollingPaginator, Table, VirtualizedTable } from "@opensea/ui-kit";
import { useHover, usePausedData } from "@opensea/ui-kit/hooks";
import { useRef } from "react";
import { useClearFilters } from "@/hooks/query-param/use-clear-filters";
import { bottomFixedVariants } from "@/styles/utils/bottom-fixed";
import { useHasSearchFilters } from "../search/hooks";
import { withSuspense } from "../suspense/WithSuspense";
import { ActivityFeedColumnsProvider } from "./ActivityFeedColumnsProvider";
import { ActivityFeedEmptyState } from "./ActivityFeedEmptyState";
import { ActivityFeedTableRow } from "./ActivityFeedTableRow";
import { ActivityFeedTableRowFragment } from "./ActivityFeedTableRow.fragment";
import { ActivityFeedTableSkeleton } from "./ActivityFeedTableSkeleton";
import { useActivityFeedColumnDelta } from "./hooks";
import { ActivityFeedTableHeader } from "./table-header";
import { useActivityFeedTableHeaderClassNames } from "./table-header/hooks";
import { useActivityFeedTableView } from "./view";
export const ActivityFeedTableCollectionFragment = graphql(`
  fragment ActivityFeedTableCollection on Collection {
    flags {
      isRarityDisabled
    }
  }
`);
const INITIAL_RECT = {
  height: 1000,
  width: 1000
};
export const ActivityFeedTable = withSuspense(function ActivityFeedTable({
  columns,
  hideCollection,
  pausable = true,
  items,
  pagination,
  scrollContainerRef,
  virtualized = true,
  variant,
  noResults,
  activityTypes,
  className,
  itemsLiveAddedAt,
  collection
}) {
  // Columns are specified as a delta from the default columns,
  const baseColumnMap = useActivityFeedColumnDelta(columns, hideCollection);
  const isRarityDisabled = collection ? readFragment(ActivityFeedTableCollectionFragment, collection)?.flags?.isRarityDisabled ?? false : false;
  const columnMap = {
    ...baseColumnMap,
    rarity: baseColumnMap.rarity && !isRarityDisabled
  };
  const {
    size
  } = useActivityFeedTableView();
  const tableBodyRef = useRef(null);
  const isHovered = useHover(tableBodyRef);
  const [renderedItems] = usePausedData({
    data: items,
    enabled: pausable,
    isPaused: isHovered && !pagination.isLoadingNext
  });
  const hasFilters = useHasSearchFilters();
  const clearFilters = useClearFilters();
  const rowContext = {
    itemsLiveAddedAt,
    variant
  };
  const headerClassNames = useActivityFeedTableHeaderClassNames();
  if (noResults) {
    return <ActivityFeedEmptyState hasSearchParams={hasFilters} onResetFilters={clearFilters} />;
  }
  return <ActivityFeedColumnsProvider columns={columnMap}>
        {virtualized ? <VirtualizedTable className={className} context={rowContext} dividers header={<ActivityFeedTableHeader activityTypes={activityTypes} className={headerClassNames} variant={variant} />} initialRect={INITIAL_RECT} itemKey={v => readFragment(ActivityFeedTableRowFragment, v)?.id} items={renderedItems} key={size} renderRow={ActivityFeedTableRow} scrollContainerRef={scrollContainerRef} scrollMode={scrollContainerRef ? "table" : "window"} size={size} tableBodyRef={tableBodyRef} {...pagination} /> : <>
            <Table className={className} context={rowContext} dividers header={<ActivityFeedTableHeader activityTypes={activityTypes} className={headerClassNames} variant={variant} />} itemKey={(_, index) => String(index)} items={renderedItems} key={size} renderRow={ActivityFeedTableRow} size={size} />
            {pagination.hasNext ? <ScrollingPaginator hasNext={pagination.hasNext} isLoadingNext={pagination.isLoadingNext} loadNext={pagination.loadNext} /> : null}
          </>}

        {pausable ? <PausedHoverable className={bottomFixedVariants()} containerRef={tableBodyRef} isHovered={isHovered} /> : null}
      </ActivityFeedColumnsProvider>;
}, {
  fallback: <ActivityFeedTableSkeleton />,
  errorFallback: <EmptyState size="lg" variant="no-data" />
});